import { useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useLingui } from "@lingui/react/macro"
import { usePage } from "@inertiajs/react"
import { useVoximplant } from "@/hooks/use-voximplant"
import { useChannel } from "@/hooks/use-channel"
import { useIsMobile } from "@/hooks/use-is-mobile"
import type { SharedVoiceConfigProps, VoiceCallRecord } from "@/types/v2/voice"
import { AUTOPLAY_BLOCKED_MESSAGE } from "@/lib/voice/errors"
import {
  isConnectedTerminalVoiceCall,
  isTerminalVoiceCall,
} from "@/lib/voice/call-record"
import { camelizeKeys } from "@/lib/utils"
import { getCsrfToken } from "@/lib/csrf"
import { toast } from "@/components/ui/sonner"
import { IncomingCallSheet } from "./incoming-call-sheet"
import { ActiveCallWidget } from "./active-call-widget"
import { AfterCallCard } from "./after-call-card"
import { VoiceWidgetShell } from "./voice-widget-shell"

// App-level softphone. Mounted ONCE in `DefaultLayout`,
// above the desktop sidebar / mobile-top-bar split, so an
// agent connects and receives inbound calls on every
// viewport (CON-82: never nest the live connection inside a
// viewport branch). Layout *shape* may branch — the overlay
// docks bottom-right on desktop and slides up as a bottom
// sheet on mobile — but the SDK connection, the ActionCable
// subscription, and all call logic live here at the page
// level, shared across both shapes.
//
// Dormant when `voiceConfig` is null/disabled (the agent has
// no Voximplant credential): no connect, no overlay.
export function Softphone() {
  const { t } = useLingui()
  const { props } = usePage<SharedVoiceConfigProps>()
  const voiceConfig = props.voiceConfig ?? null

  const {
    state,
    answer,
    decline,
    hangup,
    toggleMute,
    toggleHold,
    sendTone,
    applyBroadcast,
    reconcileActiveCall,
    resumeAudio,
    dismissCall,
  } = useVoximplant(voiceConfig)

  const isMobile = useIsMobile()

  const call = state.call

  // The in-call "Quick note" jot (CON-155). Lifted here so it
  // survives the active-call widget unmounting when the call
  // ends, and seeds the post-call card.
  const [noteDraft, setNoteDraft] = useState("")

  // Reset the jot whenever a NEW call begins (keyed on the SDK
  // call id). Keying on the id — not the phase — means typing
  // mid-call never wipes the draft, and the value is preserved
  // through the "ended" phase so it can seed the after-call
  // card before the snapshot collapses.
  const callId = call?.sdkCallId ?? null
  useEffect(() => {
    if (callId) setNoteDraft("")
  }, [callId])

  // After-call workflow card (CON-144). A normally-ended call
  // auto-collapses from the SDK store after ~1.2s, so we
  // snapshot the backend VoiceCall the moment the phase
  // reaches "ended" and keep the card mounted independently
  // until the agent saves or skips. A pre-answer "failed"
  // routing error is NOT a workflow moment — the active-call
  // widget shows its own Try again / Dismiss for that — so we
  // skip those. A fresh live call clears any lingering card.
  //
  // The snapshot can reach "ended" while the backend record is
  // still live (a spurious agent-leg SDK end mid-ring — the
  // CON-211 prod incident); mounting then showed "No answer" /
  // "Analyzing call…" for a call still ringing. So the mount
  // also waits for the server's TERMINAL record — the
  // `call.ended` broadcast path below mounts the card when the
  // server finalizes.
  const [afterCall, setAfterCall] = useState<VoiceCallRecord | null>(null)
  // The jot captured during the call that just ended, handed
  // to the after-call card as seed context (CON-155 → CON-158).
  const [afterCallSeedNote, setAfterCallSeedNote] = useState("")

  useEffect(() => {
    if (!call) return
    if (
      call.phase === "ended" &&
      call.endReason !== "failed" &&
      call.voiceCall &&
      isTerminalVoiceCall(call.voiceCall)
    ) {
      setAfterCall(call.voiceCall)
      setAfterCallSeedNote(noteDraft)
    } else if (
      call.phase === "incoming" ||
      call.phase === "connecting" ||
      call.phase === "active"
    ) {
      // A new (or still-live) call supersedes a stale card.
      setAfterCall(null)
    }
  }, [call, noteDraft])

  // The backend id of the call the softphone is (or was just)
  // on, so a `call.ended` broadcast can finalize the after-call
  // card even after the SDK snapshot collapsed — the server is
  // the source of truth for how a call ended (CON-211).
  const lastVoiceCallIdRef = useRef<number | null>(null)
  const liveVoiceCallId = call?.voiceCall?.id ?? null
  useEffect(() => {
    if (liveVoiceCallId != null) {
      lastVoiceCallIdRef.current = liveVoiceCallId
    }
  }, [liveVoiceCallId])

  // Softphone lifecycle stream (`employee:<id>`). Enriches
  // the SDK-driven call snapshot with the backend VoiceCall
  // (contact name, voice_call_id). `useChannel` camelizes
  // KEYS not VALUES → the `type` discriminator stays
  // snake/dotted.
  const onReceived = useCallback(
    (data: Record<string, unknown>) => {
      const type = data.type
      if (
        type === "call.incoming" ||
        type === "call.connected" ||
        type === "call.ended"
      ) {
        const voiceCall = data.voiceCall as VoiceCallRecord | undefined
        if (voiceCall) {
          applyBroadcast(voiceCall)
          if (
            type === "call.ended" &&
            voiceCall.id === lastVoiceCallIdRef.current &&
            isConnectedTerminalVoiceCall(voiceCall)
          ) {
            // The server finalized a call we were on — mount or
            // refresh the after-call card with the terminal
            // record, even if the SDK snapshot already collapsed.
            // Unconnected terminals (missed/declined/failed) keep
            // routing through the client's failed-call surface.
            // (CON-211)
            setAfterCall(voiceCall)
            setAfterCallSeedNote(noteDraft)
          }
        }
      }
    },
    // `useChannel` reads the callback through a ref, so the
    // `noteDraft` dep doesn't churn the subscription.
    [applyBroadcast, noteDraft],
  )

  // On (re)connect of the voice channel, reconcile against the
  // server's authoritative live call. The WebSocket may have
  // dropped mid-call (the prod failure: a missed `call.connected`
  // left the widget stuck on "Calling…", or a call ended while
  // we were offline). The server is the source of truth, so we
  // fetch the live call and heal/teardown the local snapshot.
  //
  // This is a SHARED side effect — it lives here at the
  // softphone page level (app-global in DefaultLayout), never
  // behind a viewport branch (CLAUDE.md "Build for every
  // viewport"). A plain GET, no CSRF needed.
  const onConnected = useCallback(() => {
    void fetch("/v2/channels/voice/active_call", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((raw: unknown) => {
        if (raw === null || typeof raw !== "object") return
        // The endpoint renders plain snake_case JSON (no Inertia
        // prop_transformer), so camelize it here just like
        // `useChannel` does for the broadcast path. (CON-151)
        const data = camelizeKeys(raw) as {
          voiceCall?: VoiceCallRecord | null
        }
        reconcileActiveCall(data.voiceCall ?? null)
      })
      .catch(() => {
        // A failed reconcile fetch is non-fatal — the next
        // broadcast or reconnect will retry. Stay quiet.
      })
  }, [reconcileActiveCall])

  useChannel({
    channel: "V2::VoiceChannel",
    onReceived,
    onConnected,
  })

  // "Try again" on a couldn't-connect failure re-places the
  // outbound call to the same contact through the shared
  // server-driven flow (the SDK never dials from the client).
  // We dismiss the dead snapshot first so the softphone isn't
  // stuck "busy". Null contact → no redial (the button hides).
  const retryContactId = call?.voiceCall?.contact?.id ?? null
  const handleTryAgain = useCallback(() => {
    dismissCall()
    if (!retryContactId) return
    void fetch("/v2/channels/voice/calls", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
      body: JSON.stringify({
        v2_channels_voice_calls_create_form: { contact_id: retryContactId },
      }),
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) {
          toast.error(t`We couldn't start the call. Please try again.`)
        }
      })
      .catch(() => {
        toast.error(t`We couldn't start the call. Please try again.`)
      })
  }, [dismissCall, retryContactId])

  if (!voiceConfig?.enabled) return null

  // The after-call card takes precedence over the terminal
  // active-call widget so the agent logs the call instead of
  // watching it silently collapse.
  const showAfterCall =
    afterCall !== null && (call === null || call.phase === "ended")

  const hasOverlay = call !== null || state.mediaError !== null || showAfterCall

  if (!hasOverlay) return null
  if (typeof document === "undefined") return null

  const dock = isMobile ? "inset-x-0 bottom-0" : "right-6 bottom-6"

  // Both the live-call states and the post-call card ride the
  // shared dark VoiceWidgetShell so the hand-off has no
  // light/dark seam. The after-call card itself is an INTERIM
  // dark reskin (colors only) — CON-158 rebuilds it wholesale.
  const content =
    showAfterCall && afterCall ? (
      <VoiceWidgetShell isMobile={isMobile}>
        <AfterCallCard
          voiceCall={afterCall}
          seedNote={afterCallSeedNote}
          onClose={() => {
            setAfterCall(null)
            dismissCall()
          }}
        />
      </VoiceWidgetShell>
    ) : (
      <VoiceWidgetShell isMobile={isMobile}>
        {/* Autoplay-blocked is recoverable with a user gesture →
            clickable resume. Mic-capture errors (NotAllowed /
            NotReadable) are not → plain guidance text, no dead
            affordance. */}
        {state.mediaError ? (
          <div className={`px-[18px] pt-[18px] ${call ? "" : "pb-[18px]"}`}>
            {state.mediaError === AUTOPLAY_BLOCKED_MESSAGE ? (
              <button
                type="button"
                onClick={resumeAudio}
                className="w-full rounded-[11px] bg-[#C9A24A]/15 px-3 py-2 text-left text-[13px] leading-[18px] text-[#E0C68A]"
              >
                {state.mediaError}
              </button>
            ) : (
              <p className="w-full rounded-[11px] bg-[#E5484D]/15 px-3 py-2 text-[13px] leading-[18px] text-[#F0B3B5]">
                {state.mediaError}
              </p>
            )}
          </div>
        ) : null}

        {call && call.phase === "incoming" ? (
          <IncomingCallSheet
            call={call}
            onAnswer={answer}
            onDecline={decline}
          />
        ) : null}

        {call && call.phase !== "incoming" ? (
          <ActiveCallWidget
            call={call}
            noteDraft={noteDraft}
            onNoteDraftChange={setNoteDraft}
            onToggleMute={toggleMute}
            onToggleHold={toggleHold}
            onHangup={hangup}
            onSendTone={sendTone}
            onDismiss={dismissCall}
            onTryAgain={retryContactId ? handleTryAgain : undefined}
          />
        ) : null}
      </VoiceWidgetShell>
    )

  return createPortal(
    <div className={`fixed z-50 ${dock}`} role="dialog" aria-label={t`Call`}>
      {content}
    </div>,
    document.body,
  )
}
